import { generatePath } from 'react-router-dom'
import { Tooltip, Typography } from 'antd'
import moment from 'moment'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { Assignee } from 'admin/lib/Assignee'
import { EditLink } from 'admin/lib/Common/StyledElements'
import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'
import { TemplateNameIndicator } from 'admin/pages/RecordEdit/common'

/** @type {import("antd/lib/table").ColumnsType<any>} */

const countHoursToNow = (startDate: string): number => {
  const start = moment(startDate)
  const end = moment()
  let totalHours = 0

  while (start.isBefore(end)) {
    // Check if the current day is a weekday
    if (start.isoWeekday() < 6) {
      // 1 = Monday, 5 = Friday
      totalHours += 1
    }
    start.add(1, 'hour')
  }

  return totalHours
}

const calculateSLAExpired = (timestamp, movementDateTime, status) => {
  if (
    !timestamp ||
    !status ||
    status === 'Removed' ||
    status === 'Published' ||
    status === 'AutoPublished' ||
    status === 'Void'
  ) {
    return false
  }
  const isBackDated =
    movementDateTime &&
    moment(timestamp).diff(moment(movementDateTime), 'days') > 7

  // Calculate the difference in days between the timestamp and movementDateTime
  const daysDifference = countHoursToNow(timestamp) / 24

  // If the difference is less than 1 day (or 5 days for backdated), return false (not expired)
  if (
    (isBackDated && daysDifference < 5) ||
    (!isBackDated && daysDifference < 1)
  ) {
    return false
  }

  // Otherwise, return true (expired)
  return true
}

export const columns = [
  {
    title: 'Creation date',
    dataIndex: 'timestamp',
    width: '7.25rem',
    render: (timestamp, record) => {
      return (
        <Tooltip
          title={
            record.movementDateTime ? (
              <span>
                Movement date:
                <br />
                {new Date(record.movementDateTime).toLocaleString([], {
                  timeZoneName: 'short',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                })}
              </span>
            ) : (
              ''
            )
          }
        >
          <StyledDateText>
            {new Date(timestamp).toLocaleString([], {
              timeZoneName: 'short',
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric'
            })}
          </StyledDateText>
        </Tooltip>
      )
    }
  },
  { title: 'Project', dataIndex: ['project', 'name'] },
  {
    title: 'Record type',
    dataIndex: 'moduleName',
    width: '6.375rem',
    render: moduleName => {
      return (
        <Typography.Text data-test-id="moduleNameText">
          {moduleName}
        </Typography.Text>
      )
    }
  },
  {
    title: 'Template',
    dataIndex: 'templateName',
    render: (value, record) => {
      if (value) {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TemplateNameIndicator
              estimatedAccuracy={record.estimatedAccuracy}
            />
            {value}
          </div>
        )
      }
      return ''
    }
  },
  { title: 'Movement ID', dataIndex: 'movementId', width: '12rem' },
  { title: 'Supplier / Carrier', dataIndex: 'companyName' },
  { title: 'Status', dataIndex: 'status', width: '6rem' },
  {
    title: 'Assignee',
    align: 'center',
    width: '6rem',
    render: (value, record) => {
      if (record.assignee) {
        return <Assignee fullname={record.assignee} />
      }
      return ''
    }
  },
  {
    title: 'Link',
    width: '10rem',
    render: (value, record) => {
      return (
        <div>
          {record.accounts &&
            record.accounts.map(account => {
              return (
                <EditLink
                  key={account.accountId}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={generatePath(
                    record.qcrAppliedToRaw
                      ? ROUTES.RECORD_V2.EDIT
                      : ROUTES.RECORD.EDIT,
                    {
                      accountId: account.accountId,
                      projectId: record.project.projectId,
                      moduleName: record.moduleName,
                      recordId: record.recordId
                    }
                  )}
                >
                  {account.name}: Edit
                </EditLink>
              )
            })}
        </div>
      )
    }
  },
  {
    title: 'Pages',
    align: 'center',
    width: '3.875rem',
    render: (value, record) => {
      if (record.status !== 'New') {
        const pages = record.attachmentPages ? record.attachmentPages : 1

        if (pages > 9) {
          return <div className="page-icon page-icon-red">{pages}</div>
        }
        if (pages > 2) {
          return <div className="page-icon page-icon-amber">{pages}</div>
        }
        if (pages > 1) {
          return <div className="page-icon">{pages}</div>
        }
        if (pages === -1) {
          return (
            <div className="page-icon page-icon-red page-icon-error">!</div>
          )
        }
      }
      return <span>&nbsp;</span>
    }
  },
  {
    title: 'Country',
    align: 'center',
    width: '4.75rem',
    render: (value, record) => {
      if (record?.project?.country) {
        return <Flag country={record.project.country} />
      }
      return ''
    }
  }
]

const clonedColumns = [...columns]
clonedColumns[0] = {
  title: 'Creation date',
  dataIndex: 'timestamp',
  width: '7.25rem',
  render: (timestamp, record) => {
    return (
      <Tooltip
        title={
          record.movementDateTime ? (
            <span>
              Movement date:
              <br />
              {new Date(record.movementDateTime).toLocaleString([], {
                timeZoneName: 'short',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
              })}
            </span>
          ) : (
            ''
          )
        }
      >
        <StyledDateText
          className={
            calculateSLAExpired(
              timestamp,
              record.movementDateTime,
              record.status
            )
              ? 'SLAExpired'
              : undefined
          }
        >
          {new Date(timestamp).toLocaleString([], {
            timeZoneName: 'short',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          })}
        </StyledDateText>
      </Tooltip>
    )
  }
}
export const columnsForSuperUser = [...clonedColumns]

const StyledDateText = styled(Typography.Text)`
  &.SLAExpired {
    color: ${colours.RED_DARKMODE} !important;
  }
`
